import { Button, Heading, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type Stored, type Team } from "@overmap-ai/common"
import { selectTeamsOfOrganization } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { Table } from "../../components/Tables/Table"
import { useTeamTable, useTeamTableColumns } from "../../components/Tables/TeamTable"
import { teamFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/teams/")

export const TeamSettingsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const teams = useShallowEqaulSelector(selectTeamsOfOrganization(organizationId))
	const navigate = useNavigate()

	const handleRowClick = useCallback(
		(team: Stored<Team>) => {
			void navigate({
				from: "/$organizationId/settings/teams/",
				to: "/$organizationId/settings/teams/$teamId",
				params: (prev) => ({ ...prev, teamId: team.uuid }),
			})
		},
		[navigate],
	)

	const [filteredTeams, filterValue, setFilterValue] = useTextFilter(teams, teamFilterCallback)

	const columns = useTeamTableColumns()

	const table = useTeamTable({
		data: filteredTeams,
		columns: columns,
		enableHiding: false,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">Teams</Heading>
					<Text size="sm" accentColor="base">
						Manage the teams in your organization.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex w-full grow flex-col gap-2">
					<div className="flex w-full justify-between gap-2">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<Link from="/$organizationId/settings/teams/" to="/$organizationId/settings/teams/create">
							<Button accentColor="primary" variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} /> Create
							</Button>
						</Link>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							className="w-full"
							size="sm"
							variant="surface"
							table={{ ...table }}
							onRowClick={handleRowClick}
							interactive
							emptyText="No teams."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
TeamSettingsView.displayName = "TeamSettingsView"
